import { type InjectionKey, type Ref, inject, onBeforeUnmount, onMounted, provide, ref } from 'vue'
import { useGlobalShortcut } from '../../../composables/useKeyboardShortcut'
import type { KeyboardShortcut } from '../../../lib/keyboard-shortcut'

const CHAT_DOCK_COOKIE_NAME = 'chat_dock_state'
const CHAT_DOCK_WIDTH_COOKIE_NAME = 'chat_dock_width'
const CHAT_DOCK_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
export const CHAT_DOCK_KEYBOARD_SHORTCUT: KeyboardShortcut = { key: 'j' }
const CHAT_DOCK_MOBILE_BREAKPOINT = 768
const CHAT_DOCK_DEFAULT_WIDTH = 400

/** The panels the single dock can be showing. Each is owned by exactly one feature view. */
type DockPanel = 'chat' | 'post' | 'knowledge'

export interface ChatDockContext {
  /** Whether the dock panel is shown (persisted to a cookie). */
  open: Ref<boolean>
  isMobile: Ref<boolean>
  openDock: () => void
  closeDock: () => void
  toggleDock: () => void
  panel: Ref<DockPanel>
  showPost: () => void
  showKnowledge: () => void
  showChat: () => void
  /** The user's preferred dock width in px (persisted to a cookie). */
  width: Ref<number>
  /** Set the dock width in px, persisting it and applying it as a live override. */
  setWidth: (px: number, persist?: boolean) => void
  /** Clear the user's stored width, reverting to the stylesheet default. */
  resetWidth: () => void
}

const CHAT_DOCK_KEY: InjectionKey<ChatDockContext> = Symbol('AppChatDock')

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1]
}

function readStoredWidth(): number | undefined {
  const cookie = readCookie(CHAT_DOCK_WIDTH_COOKIE_NAME)
  if (cookie === undefined) return undefined
  const parsed = Number.parseFloat(cookie)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function readDefaultWidthFromCss(): number {
  if (typeof document === 'undefined') return CHAT_DOCK_DEFAULT_WIDTH
  const parsed = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--dock-width'),
  )
  return Number.isFinite(parsed) ? parsed : CHAT_DOCK_DEFAULT_WIDTH
}

export interface ProvideChatDockOptions {
  /** Initial open state when no cookie is present. Defaults to `false`. */
  defaultOpen?: boolean
}

export function provideChatDock(options: ProvideChatDockOptions = {}): ChatDockContext {
  const cookie = readCookie(CHAT_DOCK_COOKIE_NAME)
  const stored = cookie === 'true' ? true : cookie === 'false' ? false : undefined
  const open = ref(stored ?? options.defaultOpen ?? false)

  const isMobile = ref(
    typeof window !== 'undefined' &&
      window.matchMedia(`(max-width: ${CHAT_DOCK_MOBILE_BREAKPOINT - 1}px)`).matches,
  )

  const panel = ref<DockPanel>('chat')

  const storedWidth = readStoredWidth()
  let defaultWidth = CHAT_DOCK_DEFAULT_WIDTH
  const width = ref(storedWidth ?? defaultWidth)

  function setOpen(value: boolean) {
    open.value = value
    if (typeof document !== 'undefined') {
      document.cookie = `${CHAT_DOCK_COOKIE_NAME}=${value}; path=/; max-age=${CHAT_DOCK_COOKIE_MAX_AGE}`
    }
  }
  function openDock() {
    setOpen(true)
  }
  function closeDock() {
    setOpen(false)
    panel.value = 'chat'
  }
  function toggleDock() {
    setOpen(!open.value)
  }
  function showPost() {
    panel.value = 'post'
    openDock()
  }
  function showKnowledge() {
    panel.value = 'knowledge'
    openDock()
  }
  function showChat() {
    panel.value = 'chat'
  }

  function setWidth(px: number, persist = true) {
    width.value = px
    if (typeof document === 'undefined') return
    document.documentElement.style.setProperty('--dock-width', `${px}px`)
    if (persist) {
      document.cookie = `${CHAT_DOCK_WIDTH_COOKIE_NAME}=${px}; path=/; max-age=${CHAT_DOCK_COOKIE_MAX_AGE}`
    }
  }
  function resetWidth() {
    width.value = defaultWidth
    if (typeof document !== 'undefined') {
      document.cookie = `${CHAT_DOCK_WIDTH_COOKIE_NAME}=; path=/; max-age=0`
      document.documentElement.style.removeProperty('--dock-width')
    }
  }

  let mql: MediaQueryList | undefined
  function onMediaChange(event: MediaQueryListEvent) {
    isMobile.value = event.matches
  }

  useGlobalShortcut(CHAT_DOCK_KEYBOARD_SHORTCUT, toggleDock)

  onMounted(() => {
    defaultWidth = readDefaultWidthFromCss()
    if (storedWidth === undefined) width.value = defaultWidth
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--dock-width', `${width.value}px`)
    }
    if (typeof window === 'undefined') return
    mql = window.matchMedia(`(max-width: ${CHAT_DOCK_MOBILE_BREAKPOINT - 1}px)`)
    isMobile.value = mql.matches
    mql.addEventListener('change', onMediaChange)
  })
  onBeforeUnmount(() => {
    mql?.removeEventListener('change', onMediaChange)
    if (typeof document !== 'undefined')
      document.documentElement.style.removeProperty('--dock-width')
  })

  const context: ChatDockContext = {
    open,
    isMobile,
    openDock,
    closeDock,
    toggleDock,
    width,
    setWidth,
    resetWidth,
    panel,
    showPost,
    showKnowledge,
    showChat,
  }
  provide(CHAT_DOCK_KEY, context)
  return context
}

/** Consume the chat dock context. Must be a descendant of a provider (AppShell). */
/** The dock context when one is provided, else `null`; unlike `useChatDock` it does not warn about a missing provider. */
export function useOptionalChatDock(): ChatDockContext | null {
  return inject(CHAT_DOCK_KEY, null)
}

export function useChatDock(): ChatDockContext {
  const context = inject(CHAT_DOCK_KEY)
  if (!context) {
    throw new Error('useChatDock() must be used within a provideChatDock() / AppShell')
  }
  return context
}
